import React, { useCallback, useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import LockIcon from "@mui/icons-material/Lock";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { authHeader } from "../../auth";
import { readSwissSkiFile } from "./SwissSkiUpdateDialog";

/**
 * TeamCheckAdmin — réglages de la validation des inscriptions par les
 * chefs d'équipe (page Participants, chronométreur). Ajouté le 01.10.2026.
 * Activer/désactiver, regroupement (association / inscription), colonnes
 * de points, lien public + partage WhatsApp, état des groupes (réouvrir /
 * verrouiller), liste Swiss-Ski consultable par les entraîneurs.
 */

const GROUP_FIELDS = [
    { value: "association", label: "Association" },
    { value: "inscription", label: "Inscription" },
    { value: "nation", label: "Nation" },
    { value: "club", label: "Club" },
];
const FIS_POINTS = [
    ["auto", "Auto (discipline de la course)"],
    ["gs_points", "GS"], ["sl_points", "SL"], ["sg_points", "SG"], ["dh_points", "DH"], ["ac_points", "AC"], ["cl_points", "CL (télémark)"], ["sp_points", "SP (télémark)"],
];
const SWISS_POINTS = ["Punkte", "Punkte1", "Punkte2", "Punkte3", "Punkte4", "Punkte5"];

export default function TeamCheckAdmin({ open, onClose, eventKey, onChanged }) {
    const [settings, setSettings] = useState(null);
    const [groups, setGroups] = useState([]);
    const [swissStatus, setSwissStatus] = useState(null);
    const [error, setError] = useState("");
    const [info, setInfo] = useState("");
    const [busy, setBusy] = useState(false);

    const key = encodeURIComponent(eventKey);
    const api = useCallback(
        async (url, options = {}) => {
            const res = await fetch(url, { ...options, headers: { "Content-Type": "application/json", ...authHeader(), ...(options.headers || {}) } });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.details || data.error || `HTTP ${res.status}`);
            return data;
        },
        []
    );

    const load = useCallback(async () => {
        try {
            const d = await api(`/api/team-check/${key}/settings`);
            setSettings(d.settings);
            setGroups(d.groups);
            setSwissStatus(await api("/api/team-check/swissski-list/status"));
        } catch (err) {
            setError(err.message);
        }
    }, [api, key]);

    useEffect(() => {
        if (open) {
            setError("");
            setInfo("");
            load();
        }
    }, [open, load]);

    const save = async (patch = {}) => {
        setBusy(true);
        try {
            const d = await api(`/api/team-check/${key}/settings`, { method: "POST", body: JSON.stringify({ ...settings, ...patch }) });
            setSettings(d.settings);
            await load();
            setInfo(patch.newToken ? "Nouveau lien généré — l'ancien ne fonctionne plus." : "Réglages enregistrés ✓");
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    const setLock = async (group, action) => {
        try {
            await api(`/api/team-check/${key}/group/${encodeURIComponent(group)}/${action}`, { method: "POST" });
            await load();
        } catch (err) {
            setError(err.message);
        }
    };

    const importSwiss = async (e) => {
        const file = e.target.files[0];
        e.target.value = "";
        if (!file) return;
        setBusy(true);
        try {
            const rows = await readSwissSkiFile(file);
            if (!rows.length || !("PNr" in rows[0])) throw new Error("Fichier Swiss-Ski non reconnu (colonne PNr manquante).");
            const d = await api("/api/team-check/swissski-list/import", {
                method: "POST",
                body: JSON.stringify({ rows, listName: file.name.replace(/\.[^.]+$/, "") }),
            });
            setInfo(`Liste Swiss-Ski importée : ${d.imported} athlètes ✓`);
            setSwissStatus(await api("/api/team-check/swissski-list/status"));
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    const link = settings ? `${window.location.origin}/team-check/${key}?t=${settings.token}` : "";
    const confirmed = groups.filter((g) => g.status === "confirmed").length;

    return (
        <Dialog open={open} onClose={() => (onChanged?.(), onClose())} maxWidth="md" fullWidth>
            <DialogTitle>Validation des inscriptions par les chefs d'équipe</DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
                        {error}
                    </Alert>
                )}
                {info && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo("")}>
                        {info}
                    </Alert>
                )}
                {!settings ? (
                    <Typography>Chargement…</Typography>
                ) : (
                    <>
                        <FormControlLabel
                            control={<Checkbox checked={!!settings.enabled} onChange={(e) => save({ enabled: e.target.checked })} disabled={busy} />}
                            label={<b>Validation ouverte (le lien public fonctionne)</b>}
                        />

                        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", my: 2 }}>
                            <FormControl size="small" sx={{ minWidth: 200 }}>
                                <InputLabel>Groupes par</InputLabel>
                                <Select label="Groupes par" value={settings.groupField} onChange={(e) => save({ groupField: e.target.value })}>
                                    {GROUP_FIELDS.map((g) => (
                                        <MenuItem key={g.value} value={g.value}>
                                            {g.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ minWidth: 180 }}>
                                <InputLabel>Points FIS (ajouts)</InputLabel>
                                <Select label="Points FIS (ajouts)" value={settings.fisPoints} onChange={(e) => save({ fisPoints: e.target.value })}>
                                    {FIS_POINTS.map(([v, l]) => (
                                        <MenuItem key={v} value={v}>
                                            {l}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ minWidth: 180 }}>
                                <InputLabel>Points Swiss-Ski (ajouts)</InputLabel>
                                <Select label="Points Swiss-Ski (ajouts)" value={settings.swissPoints} onChange={(e) => save({ swissPoints: e.target.value })}>
                                    {SWISS_POINTS.map((v) => (
                                        <MenuItem key={v} value={v}>
                                            {v}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                size="small"
                                label="Titre affiché (facultatif)"
                                defaultValue={settings.title}
                                onBlur={(e) => e.target.value !== settings.title && save({ title: e.target.value })}
                                sx={{ minWidth: 240 }}
                            />
                        </Box>

                        <Typography variant="subtitle2">Lien public (à diffuser par WhatsApp)</Typography>
                        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", mb: 1 }}>
                            <TextField size="small" value={link} InputProps={{ readOnly: true }} sx={{ flex: 1, minWidth: 300 }} />
                            <Tooltip title="Copier">
                                <IconButton onClick={() => navigator.clipboard?.writeText(link).then(() => setInfo("Lien copié ✓"))}>
                                    <ContentCopyIcon />
                                </IconButton>
                            </Tooltip>
                            <Button
                                variant="contained"
                                color="success"
                                disabled={!settings.enabled}
                                onClick={() =>
                                    window.open(
                                        `https://wa.me/?text=${encodeURIComponent(
                                            `Entries ${settings.title || eventKey}: please check and confirm your athletes before the team captains' meeting 👉 ${link}`
                                        )}`,
                                        "_blank",
                                        "noopener,noreferrer"
                                    )
                                }
                            >
                                Partager WhatsApp
                            </Button>
                            <Button size="small" color="warning" onClick={() => window.confirm("Générer un nouveau lien ? L'ancien ne fonctionnera plus.") && save({ newToken: true })}>
                                Nouveau lien
                            </Button>
                        </Box>
                        {!settings.enabled && (
                            <Typography variant="caption" color="warning.main">
                                Validation fermée : le lien affiche « non ouverte » tant que la case n'est pas cochée.
                            </Typography>
                        )}

                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Groupes — {confirmed} / {groups.length} confirmé(s)
                        </Typography>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Groupe</TableCell>
                                    <TableCell align="right">Athlètes</TableCell>
                                    <TableCell>État</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {groups.map((g) => (
                                    <TableRow key={g.key}>
                                        <TableCell>{g.key}</TableCell>
                                        <TableCell align="right">{g.count}</TableCell>
                                        <TableCell>
                                            {g.status === "confirmed" ? (
                                                <Chip size="small" color="success" label={`Confirmé — ${g.confirmedBy || ""}${g.confirmedAt ? `, ${new Date(g.confirmedAt).toLocaleString("fr-CH")}` : ""}`} />
                                            ) : g.status === "modified" ? (
                                                <Chip size="small" color="error" label="Modifié — non confirmé" />
                                            ) : (
                                                <Chip size="small" variant="outlined" color="warning" label="À valider" />
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            {g.status === "confirmed" ? (
                                                <Button size="small" startIcon={<LockOpenIcon />} onClick={() => setLock(g.key, "unlock")}>
                                                    Réouvrir
                                                </Button>
                                            ) : (
                                                <Button size="small" startIcon={<LockIcon />} onClick={() => setLock(g.key, "lock")}>
                                                    Verrouiller
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>

                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Listes consultables par les entraîneurs
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Liste FIS : celle importée dans la page Participants. Liste Swiss-Ski :{" "}
                            {swissStatus?.count ? `${swissStatus.count} athlètes (${swissStatus.listName || "—"})` : "pas encore importée"}.
                        </Typography>
                        <Button component="label" size="small" variant="outlined" startIcon={<UploadFileIcon />} disabled={busy}>
                            Importer / mettre à jour la liste Swiss-Ski
                            <input type="file" accept=".xlsx,.xls,.csv,.txt,.tsv" hidden onChange={importSwiss} />
                        </Button>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={() => {
                        onChanged?.();
                        onClose();
                    }}
                >
                    Fermer
                </Button>
            </DialogActions>
        </Dialog>
    );
}